import { useRef, type ReactElement, type ReactNode } from "react";
import { Button } from "../Button";
import { Text } from "../Text";
import { Dialog } from "./Dialog";

/**
 * ConfirmDialog: the confirmation step for a consequential action.
 * Replaces `window.confirm`. For `tone="danger"` the initial focus lands on
 * Cancel; otherwise on Confirm.
 */

export type ConfirmDialogTone = "default" | "danger";

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: ReactNode;
    /** Default `"Confirm"`. */
    confirmLabel?: string;
    /** Default `"Cancel"`. */
    cancelLabel?: string;
    tone?: ConfirmDialogTone;
    /** Confirm shows a spinner; Cancel and dismissal are blocked. */
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    testId?: string;
}

export function ConfirmDialog({
    open,
    title,
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "default",
    busy = false,
    onConfirm,
    onCancel,
    testId,
}: ConfirmDialogProps): ReactElement | null {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    return (
        <Dialog
            open={open}
            onClose={busy ? () => undefined : onCancel}
            title={title}
            size="sm"
            initialFocusRef={tone === "danger" ? cancelRef : confirmRef}
            closeOnScrimClick={!busy}
            closeOnEscape={!busy}
            testId={testId}
            footer={
                <>
                    <Button ref={cancelRef} variant="ghost" onClick={onCancel} disabled={busy}>
                        {cancelLabel}
                    </Button>
                    <Button
                        ref={confirmRef}
                        variant={tone === "danger" ? "danger" : "primary"}
                        onClick={onConfirm}
                        busy={busy}
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            {typeof message === "string" ? <Text role="body">{message}</Text> : message}
        </Dialog>
    );
}
